import React, { useEffect, useLayoutEffect, useRef } from 'react';
import { makeStyles, alpha } from '@material-ui/core/styles';
import { addZ } from '../Utils/utils';

const ITEM_HEIGHT = 48;
const SETTLE_DELAY = 160;
const LOOP_COPIES = 5;
const CENTER_COPY = 2;

const wrap = (value, count) => ((value % count) + count) % count;

const useStyles = makeStyles((theme) => ({
  root: { minWidth: 0, flex: 1, textAlign: 'center' },
  label: {
    display: 'block',
    marginBottom: 8,
    fontSize: '0.875rem',
    color: theme.palette.text.secondary
  },
  frame: {
    position: 'relative',
    '&:before': {
      content: '""',
      position: 'absolute',
      top: '50%',
      left: 0,
      right: 0,
      height: ITEM_HEIGHT,
      transform: 'translateY(-50%)',
      backgroundColor: alpha(
        theme.palette.primary.main,
        theme.palette.type === 'dark' ? 0.2 : 0.14
      ),
      borderTop: `1px solid ${alpha(theme.palette.primary.main, 0.5)}`,
      borderBottom: `1px solid ${alpha(theme.palette.primary.main, 0.5)}`,
      pointerEvents: 'none'
    }
  },
  wheel: {
    height: ITEM_HEIGHT * 3,
    padding: `${ITEM_HEIGHT}px 0`,
    boxSizing: 'border-box',
    overflowY: 'auto',
    overscrollBehaviorY: 'contain',
    scrollSnapType: 'y mandatory',
    scrollbarWidth: 'none',
    '&::-webkit-scrollbar': { display: 'none' },
    WebkitOverflowScrolling: 'touch',
    touchAction: 'pan-y',
    userSelect: 'none',
    cursor: 'grab',
    position: 'relative',
    maskImage: 'linear-gradient(transparent, black 30%, black 70%, transparent)',
    '&:focus-visible': { outline: `2px solid ${theme.palette.secondary.main}`, outlineOffset: -2 },
    '&[aria-disabled="true"]': { opacity: 0.5, cursor: 'default' }
  },
  option: {
    height: ITEM_HEIGHT,
    lineHeight: `${ITEM_HEIGHT}px`,
    scrollSnapAlign: 'center',
    fontSize: '1.375rem',
    fontVariantNumeric: 'tabular-nums',
    color: theme.palette.text.secondary,
    '&[data-selected="true"]': {
      fontSize: '1.875rem',
      fontWeight: 500,
      color: theme.palette.text.primary
    }
  }
}));

export default function TimeWheel({ label, value, max, disabled, onChange, onBusyChange }) {
  const classes = useStyles();
  const count = max + 1;
  const cycleHeight = count * ITEM_HEIGHT;
  const wheel = useRef(null);
  const selected = useRef(value);
  const timer = useRef(null);
  const pointer = useRef(null);
  const dragged = useRef(false);
  const positioned = useRef(false);
  const rebaseTarget = useRef(null);
  const callbacks = useRef({ onChange, onBusyChange });
  callbacks.current = { onChange, onBusyChange };

  useLayoutEffect(() => {
    selected.current = value;
    // Leave native momentum alone when the value came from scrolling this wheel.
    if (
      !positioned.current ||
      wrap(Math.round(wheel.current.scrollTop / ITEM_HEIGHT), count) !== value
    ) {
      positioned.current = true;
      rebaseTarget.current = (CENTER_COPY * count + value) * ITEM_HEIGHT;
      wheel.current.scrollTop = rebaseTarget.current;
    }
  }, [value, count]);
  useEffect(() => () => clearTimeout(timer.current), []);

  const settle = () => {
    clearTimeout(timer.current);
    timer.current = setTimeout(() => {
      if (pointer.current) return;
      // Identical repeated rows let us return to the middle without a visible jump.
      rebaseTarget.current = (CENTER_COPY * count + selected.current) * ITEM_HEIGHT;
      wheel.current.scrollTop = rebaseTarget.current;
      callbacks.current.onBusyChange(false);
    }, SETTLE_DELAY);
  };

  const handleScroll = () => {
    const top = wheel.current.scrollTop;
    const rebased = rebaseTarget.current !== null && Math.abs(top - rebaseTarget.current) < 0.5;
    rebaseTarget.current = null;
    if (rebased) return;
    if (disabled) {
      rebaseTarget.current = (CENTER_COPY * count + value) * ITEM_HEIGHT;
      wheel.current.scrollTop = rebaseTarget.current;
      return;
    }
    callbacks.current.onBusyChange(true);
    const next = wrap(Math.round(top / ITEM_HEIGHT), count);
    if (next !== selected.current) {
      selected.current = next;
      callbacks.current.onChange(next);
    }
    // Keep a full spare cycle on either side, even during a long continuous drag.
    if (top < cycleHeight || top >= cycleHeight * (LOOP_COPIES - 1)) {
      const centered = cycleHeight * CENTER_COPY + wrap(top, cycleHeight);
      if (pointer.current) pointer.current.top += centered - top;
      rebaseTarget.current = centered;
      wheel.current.scrollTop = centered;
    }
    settle();
  };

  const select = (next) => {
    if (disabled) return;
    clearTimeout(timer.current);
    next = wrap(next, count);
    selected.current = next;
    rebaseTarget.current = (CENTER_COPY * count + next) * ITEM_HEIGHT;
    wheel.current.scrollTop = rebaseTarget.current;
    callbacks.current.onChange(next);
    callbacks.current.onBusyChange(false);
  };

  const handleKeyDown = (event) => {
    const targets = {
      ArrowUp: value - 1,
      ArrowDown: value + 1,
      PageUp: value - 5,
      PageDown: value + 5,
      Home: 0,
      End: max
    };
    if (event.key in targets) {
      event.preventDefault();
      select(targets[event.key]);
    }
  };

  const finishPointer = (event) => {
    const start = pointer.current;
    pointer.current = null;
    wheel.current.style.scrollSnapType = '';
    if (
      event.type === 'pointerup' &&
      start?.type === 'mouse' &&
      !dragged.current &&
      start.option !== undefined
    ) {
      select(start.option);
    }
    settle();
  };

  return (
    <div className={classes.root}>
      <span className={classes.label}>{label}</span>
      <div className={classes.frame}>
        <div
          className={classes.wheel}
          ref={wheel}
          role="spinbutton"
          tabIndex={disabled ? -1 : 0}
          aria-label={label}
          aria-valuemin={0}
          aria-valuemax={max}
          aria-valuenow={value}
          aria-valuetext={addZ(value)}
          aria-disabled={disabled || undefined}
          onScroll={handleScroll}
          onKeyDown={handleKeyDown}
          onPointerDown={(event) => {
            if (disabled || (event.pointerType === 'mouse' && event.button !== 0)) return;
            dragged.current = false;
            pointer.current = {
              y: event.clientY,
              top: wheel.current.scrollTop,
              type: event.pointerType,
              option: event.target.closest('[data-time-option]')?.dataset.timeOption
            };
            callbacks.current.onBusyChange(true);
            if (event.pointerType === 'mouse') {
              event.preventDefault();
              wheel.current.focus();
              wheel.current.setPointerCapture(event.pointerId);
              wheel.current.style.scrollSnapType = 'none';
            }
          }}
          onPointerMove={(event) => {
            const start = pointer.current;
            if (!start) return;
            const delta = start.y - event.clientY;
            if (Math.abs(delta) > 5) dragged.current = true;
            if (start.type === 'mouse' && dragged.current) {
              wheel.current.scrollTop = start.top + delta;
            }
          }}
          onPointerUp={finishPointer}
          onPointerCancel={finishPointer}
          onLostPointerCapture={finishPointer}
          onClick={(event) => {
            const option = event.target.closest('[data-time-option]');
            if (option && !dragged.current) select(Number(option.dataset.timeOption));
          }}
        >
          {Array.from({ length: count * LOOP_COPIES }, (_, index) => (
            <div
              key={index}
              className={classes.option}
              data-time-option={index % count}
              data-selected={index % count === value}
              aria-hidden="true"
            >
              {addZ(index % count)}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
